import axios from 'axios';

/** The `parameters` blob stored on a gateway config row. */
interface GatewayConfigParameters {
  /**
   * JSON-encoded authorised shops object, e.g.
   * `{"retail":{"123":["shop-1","shop-2"]}}`.
   */
  authorizedShops: string;
  /**
   * Platform version of the gateway config. The API returns this as a string
   * or number; coerce with Number() before comparing. Missing or non-numeric
   * values are treated as not enabled (fail closed).
   */
  platformVersion?: string | number;
}

/** A single gateway config as returned by the Retail API. */
interface GatewayConfig {
  gatewayID: string;
  parameters: GatewayConfigParameters;
}

/** Top-level shape of the `/GatewayConfig.json` response. */
interface GatewayConfigResponse {
  GatewayConfig?: GatewayConfig | GatewayConfig[];
}

/**
 * Parsed structure of `parameters.authorizedShops`.
 * `retail[accountID]` is the list of shop IDs authorised for that account.
 */
interface AuthorizedShops {
  retail?: Record<string, string[]>;
}

/**
 * Service for querying LS-Pay gateway configuration data.
 */
export default class PaymentGatewayService {
  /**
   * Returns the set of shop IDs that are authorised on an LS-Pay v3+ gateway
   * config for the current account.
   *
   * A shop is considered "LS-Pay enabled" when it appears in
   * `parameters.authorizedShops.retail[accountID]` of a config whose
   * `gatewayID === "lightspeed-payments"` and `parameters.platformVersion >= 3`.
   * This matches the backend definition in `PaymentStatusHelper::getGatewayIDFromGatewayConfigs`.
   *
   * Fails closed: any request or parsing failure returns an empty set so that
   * callers treat all shops as not enabled rather than inadvertently enabling them.
   */
  static async getLsPayEnabledShopIds(): Promise<Set<string>> {
    const accountID = window.merchantos.account.id;

    try {
      const response = await axios.get<GatewayConfigResponse>(
        `/API/Account/${accountID}/GatewayConfig.json`
      );

      const raw = response?.data?.GatewayConfig;
      if (!raw) return new Set<string>();

      // Normalise single-object vs array response (mirrors getShopsWithRegisters).
      const configs: GatewayConfig[] = Array.isArray(raw) ? raw : [raw];

      // Only consider LS-Pay v3+ gateway configs (matches PaymentStatusHelper backend logic).
      const lsPayV3Configs = configs.filter(
        (config) =>
          config.gatewayID === 'lightspeed-payments' &&
          Number(config.parameters.platformVersion) >= 3
      );

      const enabledShopIds = lsPayV3Configs.reduce<Set<string>>((acc, config) => {
        let parsed: unknown;
        try {
          // Isolate JSON parse so a single malformed config does not abort the iteration.
          parsed = JSON.parse(config.parameters.authorizedShops);
        } catch {
          return acc;
        }

        // Guard against non-object parse results (e.g. JSON.parse("null") returns null),
        // which would throw when accessing .retail and silently abort the whole reduce.
        if (typeof parsed !== 'object' || parsed === null) return acc;

        const authorizedShops = parsed as AuthorizedShops;
        const shopIds = authorizedShops.retail?.[String(accountID)];
        if (!Array.isArray(shopIds)) return acc;

        shopIds.forEach((id) => acc.add(String(id)));
        return acc;
      }, new Set<string>());

      return enabledShopIds;
    } catch {
      // Request failure — fail closed so shops are not inadvertently treated as enabled.
      return new Set<string>();
    }
  }
}
